import { useEffect, useMemo, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell,
} from "recharts";
import {
  adminListOrders,
  adminGetOrder,
  adminUpdateOrder,
  adminRefundOrder,
  adminSendInvoice,
  adminGetStats,
  adminGetAnalytics,
} from "@/lib/api";

/**
 * Orders dashboard — lists paid/preparing/shipped/etc. orders and lets the
 * admin move them through the fulfilment lifecycle, add tracking details,
 * or issue a Stripe refund.
 *
 * Composed with the existing catalog admin — mounted as a second tab.
 */

const STATUS_STYLES = {
  paid:       { label: "Paid",       bg: "#DCE8A1", fg: "#2F4A0C" },
  preparing:  { label: "Preparing",  bg: "#FFE9D6", fg: "#7A4A16" },
  shipped:    { label: "Shipped",    bg: "#F4CB47", fg: "#241C18" },
  delivered:  { label: "Delivered",  bg: "#E6E1D6", fg: "#241C18" },
  refunded:   { label: "Refunded",   bg: "#F9B4A5", fg: "#5C1A0C" },
  cancelled:  { label: "Cancelled",  bg: "#E5E5E5", fg: "#4A4A4A" },
};

const NEXT_STATUS = {
  paid: "preparing",
  preparing: "shipped",
  shipped: "delivered",
};

// Human-friendly labels for the Stripe tax-id type codes shoppers pick
// during checkout (Stripe docs: https://stripe.com/docs/billing/customer/tax-ids).
// The most-common ones for a Portugal-based boutique are listed first;
// anything not on the list falls back to the raw code, upper-cased.
const TAX_ID_LABELS = {
  pt_nif: "NIF (PT)",
  eu_vat: "EU VAT",
  es_cif: "CIF (ES)",
  fr_vat: "VAT (FR)",
  de_vat: "VAT (DE)",
  it_vat: "P.IVA (IT)",
  gb_vat: "VAT (UK)",
  us_ein: "EIN (US)",
  br_cnpj: "CNPJ (BR)",
};

const StatusPill = ({ status }) => {
  const s = STATUS_STYLES[status] || STATUS_STYLES.paid;
  return (
    <span
      className="font-badge text-[10px] tracking-[0.16em] uppercase px-2.5 py-1 rounded-full"
      style={{ background: s.bg, color: s.fg }}
    >
      {s.label}
    </span>
  );
};

const StatCard = ({ label, value, sub, testid }) => (
  <div
    data-testid={testid}
    className="flex-1 min-w-[140px] bg-white border border-[#F1DCC5] rounded-2xl p-5"
  >
    <div className="font-badge text-[10px] tracking-[0.24em] text-[#8A7568]">{label}</div>
    <div className="font-serif-display text-3xl md:text-4xl text-[#241C18] mt-2">{value}</div>
    {sub ? <div className="text-xs text-[#8A7568] mt-1">{sub}</div> : null}
  </div>
);

const money = (n) => `€${Number(n || 0).toFixed(2)}`;

// ── Analytics panel ────────────────────────────────────────────────
const CHART_COLOURS = ["#F4CB47", "#C97A63", "#DCE8A1", "#F9B4A5", "#241C18", "#E9C4A0", "#8AB6A4", "#F1DCC5"];

const AnalyticsPanel = ({ data }) => {
  const [range, setRange] = useState("daily");
  if (!data) return null;

  const series = data[range] || [];
  const label = { daily: "Last 30 days", weekly: "Last 12 weeks", monthly: "Last 12 months" }[range];
  const xKey = { daily: "date", weekly: "week_start", monthly: "month" }[range];

  const fmtX = (v) => {
    if (range === "daily") {
      const d = new Date(v);
      return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    }
    if (range === "weekly") {
      const d = new Date(v);
      return `w/o ${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
    }
    const [y, m] = v.split("-");
    return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString(undefined, { month: "short", year: "2-digit" });
  };

  const totalPeriod = series.reduce((s, r) => s + (r.revenue || 0), 0);
  const totalOrders = series.reduce((s, r) => s + (r.count || 0), 0);
  const avgOrder = totalOrders ? totalPeriod / totalOrders : 0;

  return (
    <div data-testid="admin-analytics-panel" className="mt-8 bg-white border border-[#F1DCC5] rounded-2xl p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <div className="font-badge text-[10px] tracking-[0.24em] text-[#8A7568]">SALES · {label.toUpperCase()}</div>
          <div className="font-serif-display text-2xl md:text-3xl text-[#241C18] mt-1">
            {money(totalPeriod)} · <span className="text-[#8A7568] text-xl">{totalOrders} order{totalOrders === 1 ? "" : "s"}</span>
          </div>
          <div className="text-xs text-[#8A7568] mt-1">Avg order value {money(avgOrder)}</div>
        </div>
        <div className="flex gap-1 bg-[#F9F1E6] p-1 rounded-full text-[11px] font-badge tracking-[0.14em] uppercase">
          {["daily", "weekly", "monthly"].map((r) => (
            <button
              key={r}
              data-testid={`admin-analytics-range-${r}`}
              onClick={() => setRange(r)}
              className={`px-3 py-1.5 rounded-full transition ${range === r ? "bg-[#241C18] text-[var(--gl-orange)]" : "text-[#8A7568] hover:text-[#241C18]"}`}
            >
              {r}
            </button>
          ))}
        </div>
      </div>

      <div style={{ width: "100%", height: 240 }}>
        <ResponsiveContainer>
          <BarChart data={series} margin={{ top: 8, right: 8, bottom: 0, left: -8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F1DCC5" vertical={false} />
            <XAxis dataKey={xKey} tickFormatter={fmtX} tick={{ fontSize: 10, fill: "#8A7568" }} axisLine={{ stroke: "#F1DCC5" }} interval="preserveStartEnd" />
            <YAxis tickFormatter={(v) => `€${v}`} tick={{ fontSize: 10, fill: "#8A7568" }} axisLine={{ stroke: "#F1DCC5" }} />
            <Tooltip
              formatter={(v, name) => (name === "revenue" ? [money(v), "Merchandise revenue"] : [v, "Orders"])}
              labelFormatter={(v) => fmtX(v)}
              contentStyle={{ borderRadius: 8, border: "1px solid #F1DCC5", fontSize: 12 }}
            />
            <Bar dataKey="revenue" fill="#F4CB47" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mt-6">
        {/* By category */}
        <div>
          <div className="font-badge text-[10px] tracking-[0.24em] text-[#8A7568] mb-2">REVENUE BY CATEGORY</div>
          {(data.by_category?.length || 0) === 0 ? (
            <div className="text-xs text-[#8A7568] italic py-6">No data yet.</div>
          ) : (
            <div style={{ width: "100%", height: 200 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={data.by_category}
                    dataKey="revenue"
                    nameKey="category"
                    cx="50%"
                    cy="50%"
                    outerRadius={70}
                    innerRadius={40}
                  >
                    {data.by_category.map((_e, i) => (
                      <Cell key={i} fill={CHART_COLOURS[i % CHART_COLOURS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v, _n, ctx) => [money(v), ctx.payload.category]} contentStyle={{ borderRadius: 8, border: "1px solid #F1DCC5", fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-[#6B6862]">
            {data.by_category?.slice(0, 6).map((c, i) => (
              <span key={c.category} className="inline-flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm" style={{ background: CHART_COLOURS[i % CHART_COLOURS.length] }} />
                {c.category} · {money(c.revenue)}
              </span>
            ))}
          </div>
        </div>

        {/* Top products */}
        <div>
          <div className="font-badge text-[10px] tracking-[0.24em] text-[#8A7568] mb-2">BEST SELLERS · BY UNITS</div>
          {(data.top_products?.length || 0) === 0 ? (
            <div className="text-xs text-[#8A7568] italic py-6">No units sold yet.</div>
          ) : (
            <div className="space-y-1.5">
              {data.top_products.slice(0, 6).map((p) => (
                <div key={p.slug || p.name} className="flex items-center justify-between gap-3 text-[13px]">
                  <div className="truncate text-[#241C18]">{p.name}</div>
                  <div className="shrink-0 flex items-center gap-3">
                    <span className="text-[#8A7568] text-xs">{p.units} pcs</span>
                    <span className="text-[#241C18] font-typewriter text-[11px]">{money(p.revenue)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const OrderDetail = ({ order: initialOrder, onClose, onSaved }) => {
  // Fetch a fresh copy on open so the backend's lazy phone-backfill (for
  // legacy orders that pre-date the customer_phone field) runs and the
  // modal always shows the latest values.
  const [order, setOrder] = useState(initialOrder);
  useEffect(() => {
    let cancelled = false;
    adminGetOrder(initialOrder.id)
      .then((fresh) => {
        if (!cancelled && fresh) setOrder(fresh);
      })
      .catch(() => {
        /* fall back to the list-row copy silently */
      });
    return () => {
      cancelled = true;
    };
  }, [initialOrder.id]);

  const [status, setStatus] = useState(order.status);
  const [trackingNumber, setTrackingNumber] = useState(order.tracking_number || "");
  const [trackingUrl, setTrackingUrl] = useState(order.tracking_url || "");
  const [notes, setNotes] = useState(order.admin_notes || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const addr = order.shipping_address || {};

  const save = async () => {
    setBusy(true);
    setErr("");
    try {
      const updated = await adminUpdateOrder(order.id, {
        status,
        tracking_number: trackingNumber,
        tracking_url: trackingUrl,
        admin_notes: notes,
      });
      onSaved(updated);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const [refundOpen, setRefundOpen] = useState(false);
  const [refundAmount, setRefundAmount] = useState("");
  const [refundIncludeShipping, setRefundIncludeShipping] = useState(false);
  const [refundReason, setRefundReason] = useState("");
  // Per-item picker — array of indexes into order.items that the admin
  // has ticked as "returned". When non-empty, the refund amount snaps
  // to the sum of unit_price × qty for those items and the backend
  // restocks only those variants.
  const [refundSelectedIdx, setRefundSelectedIdx] = useState([]);

  // ─── Invoice email (manual send from admin) ─────────────────────
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [invoiceBusy, setInvoiceBusy] = useState(false);
  const [invoiceMsg, setInvoiceMsg] = useState("");

  const sendInvoice = async () => {
    setInvoiceBusy(true);
    setInvoiceMsg("");
    try {
      const updated = await adminSendInvoice(order.id, invoiceFile || undefined);
      setOrder(updated);
      setInvoiceFile(null);
      setInvoiceMsg(`Sent to ${updated.customer_email}`);
    } catch (e) {
      setInvoiceMsg(e?.response?.data?.detail || "Send failed");
    } finally {
      setInvoiceBusy(false);
    }
  };

  const subtotal = Number(order.subtotal || 0);
  const shipping = Number(order.shipping_amount || 0);
  const total = Number(order.total || 0);
  const alreadyRefunded = Number(order.refunded_amount || 0);
  const remaining = Math.max(0, Number((total - alreadyRefunded).toFixed(2)));

  const openRefund = () => {
    // Preselect merchandise-only refund of the remaining subtotal —
    // shipping stays with GOYA by default per the store's policy.
    const merchRemaining = Math.max(0, Number((subtotal - alreadyRefunded).toFixed(2)));
    setRefundAmount(merchRemaining ? merchRemaining.toFixed(2) : remaining.toFixed(2));
    setRefundIncludeShipping(false);
    setRefundReason("");
    setRefundSelectedIdx([]);
    setErr("");
    setRefundOpen(true);
  };

  // Sum of unit_price × qty for the ticked items. Used to seed the
  // amount input from the toggle so the two stay in sync.
  const toggleItem = (i) => {
    setRefundSelectedIdx((prev) => {
      const next = prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i];
      // Auto-sync the amount input so the admin sees the running total.
      const nextTotal = (order.items || []).reduce((s, it, idx) => (
        next.includes(idx) ? s + Number(it.unit_price || 0) * Number(it.quantity || 1) : s
      ), 0);
      if (next.length > 0) {
        setRefundAmount(nextTotal.toFixed(2));
        setRefundIncludeShipping(false);
      }
      return next;
    });
  };

  const runRefund = async () => {
    const amt = parseFloat(refundAmount);
    if (!amt || amt <= 0) { setErr("Enter a valid refund amount"); return; }
    if (amt > remaining + 0.01) { setErr(`Amount exceeds remaining refundable balance (€${remaining.toFixed(2)})`); return; }
    setBusy(true); setErr("");
    try {
      await adminRefundOrder(order.id, {
        // When items are picked, the backend recomputes the exact
        // amount from the selected items and restocks only those variants.
        line_item_indexes: refundSelectedIdx.length > 0 ? refundSelectedIdx : null,
        amount: refundSelectedIdx.length > 0 ? null : amt,
        include_shipping: refundIncludeShipping,
        reason: refundReason.trim() || null,
      });
      const refreshed = await adminUpdateOrder(order.id, {});
      onSaved(refreshed);
      setRefundOpen(false);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Refund failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-[#241C18]/50 flex items-start justify-center p-4 py-8 overflow-y-auto"
      onClick={onClose}
      data-testid="admin-order-detail-modal"
    >
      <div
        className="bg-white w-full max-w-3xl rounded-2xl border border-[#F1DCC5] shadow-2xl my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-[#F1DCC5] px-6 py-4 flex items-start justify-between rounded-t-2xl">
          <div>
            <div className="font-serif-display text-2xl text-[#241C18]">
              {order.order_number}
            </div>
            <div className="text-xs text-[#8A7568] mt-1">
              {order.customer_name || "—"} · {order.customer_email} ·{" "}
              {new Date(order.created_at).toLocaleString()}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <StatusPill status={order.status} />
            <button
              data-testid="admin-order-close"
              onClick={onClose}
              className="text-xs underline underline-offset-2 text-[#8A7568] hover:text-[#241C18]"
            >
              Close
            </button>
          </div>
        </div>

        <div className="p-6 space-y-8">
          {/* Items */}
          <section>
            <div className="font-badge text-[10px] tracking-[0.24em] text-[#8A7568] mb-3">
              ITEMS
            </div>
            <div className="space-y-3">
              {(order.items || []).map((it, i) => (
                <div key={i} className="flex items-center gap-4 py-2 border-b border-[#F1DCC5] last:border-0">
                  {it.image ? (
                    <img src={it.image} alt="" className="w-14 h-16 object-cover rounded" />
                  ) : (
                    <div className="w-14 h-16 bg-[#FFE9D6] rounded" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-serif-display text-base text-[#241C18] truncate">{it.name}</div>
                    <div className="text-xs text-[#8A7568]">
                      {it.variant_name || ""} · Qty {it.quantity}
                    </div>
                  </div>
                  <div className="text-sm text-[#241C18]">{money(it.unit_price * it.quantity)}</div>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-[#F1DCC5] text-sm space-y-1">
              <div className="flex justify-between"><span>Subtotal</span><span>{money(order.subtotal)}</span></div>
              <div className="flex justify-between"><span>Shipping ({order.shipping_method || "—"})</span><span>{money(order.shipping_amount)}</span></div>
              <div className="flex justify-between text-base font-semibold pt-2 border-t border-[#241C18]">
                <span>Total</span>
                <span>{money(order.total)}</span>
              </div>
            </div>
          </section>

          {/* Shipping address */}
          <section>
            <div className="font-badge text-[10px] tracking-[0.24em] text-[#8A7568] mb-3">
              SHIP TO
            </div>
            <div className="text-sm text-[#241C18] leading-relaxed">
              <div>{addr.name || "—"}</div>
              {addr.line1 && <div>{addr.line1}</div>}
              {addr.line2 && <div>{addr.line2}</div>}
              <div>{[addr.postal_code, addr.city].filter(Boolean).join(" ")}</div>
              <div>{addr.country || ""}</div>
              {order.customer_phone && (
                <div className="mt-2 text-sm">
                  <span className="font-badge text-[10px] tracking-[0.24em] text-[#8A7568] mr-2">PHONE</span>
                  <a
                    href={`tel:${order.customer_phone}`}
                    data-testid="admin-order-customer-phone"
                    className="text-[#241C18] underline underline-offset-2 hover:text-[var(--gl-orange-hover)]"
                  >
                    {order.customer_phone}
                  </a>
                </div>
              )}
              {order.customer_tax_id && (
                <div className="mt-1 text-sm" data-testid="admin-order-customer-tax-id">
                  <span className="font-badge text-[10px] tracking-[0.24em] text-[#8A7568] mr-2">
                    {TAX_ID_LABELS[order.customer_tax_id_type] || (order.customer_tax_id_type ? order.customer_tax_id_type.replace(/_/g, " ").toUpperCase() : "TAX ID")}
                  </span>
                  <span className="text-[#241C18] font-typewriter">{order.customer_tax_id}</span>
                </div>
              )}
            </div>
          </section>

          {/* Send invoice email — attaches an admin-uploaded PDF (optional). */}
          <section data-testid="admin-order-invoice-section">
            <div className="font-badge text-[10px] tracking-[0.24em] text-[#8A7568] mb-3">
              INVOICE
            </div>
            <div className="text-xs text-[#8A7568] mb-3">
              Sends an email to <span className="text-[#241C18]">{order.customer_email}</span> using the <span className="text-[#241C18] font-typewriter">order_invoice</span> template
              {" "}(editable in <span className="text-[#241C18]">/admin → Emails</span>).
              {" "}Attach a PDF invoice to include it with the message.
            </div>
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
              <label className="flex-1 min-w-0 w-full">
                <input
                  data-testid="admin-order-invoice-file"
                  type="file"
                  accept="application/pdf,.pdf"
                  onChange={(e) => setInvoiceFile(e.target.files?.[0] || null)}
                  className="block w-full text-xs text-[#241C18]
                             file:mr-3 file:px-3 file:py-1.5 file:rounded-full
                             file:border file:border-[#F1DCC5] file:bg-white
                             file:text-xs file:font-badge file:tracking-[0.14em] file:uppercase file:text-[#241C18]
                             hover:file:bg-[#FFE9D6]/40 cursor-pointer"
                />
              </label>
              <button
                type="button"
                data-testid="admin-order-invoice-send"
                onClick={sendInvoice}
                disabled={invoiceBusy || !order.customer_email}
                className="shrink-0 whitespace-nowrap px-4 py-2 rounded-full bg-[#241C18] text-[#F1ECE2] text-xs font-badge tracking-[0.14em] uppercase hover:bg-[var(--gl-orange)] hover:text-[var(--gl-orange-text)] disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                {invoiceBusy ? "Sending…" : (order.invoice_sent_at ? "Resend invoice" : "Send invoice")}
              </button>
            </div>
            {invoiceMsg && (
              <div
                data-testid="admin-order-invoice-msg"
                className={`mt-2 text-xs ${invoiceMsg.startsWith("Sent") ? "text-[#2F4A0C]" : "text-[#8B2A18]"}`}
              >
                {invoiceMsg}
              </div>
            )}
            {order.invoice_sent_at && (
              <div className="mt-3 text-xs text-[#8A7568]" data-testid="admin-order-invoice-last-sent">
                Last sent {new Date(order.invoice_sent_at).toLocaleString()}
                {order.invoice_attachment_name ? ` · ${order.invoice_attachment_name}` : " · no attachment"}
              </div>
            )}
          </section>

          {/* Fulfilment */}
          <section>
            <div className="font-badge text-[10px] tracking-[0.24em] text-[#8A7568] mb-3">
              FULFILMENT
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs text-[#8A7568]">Status</span>
                <select
                  data-testid="admin-order-status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="w-full mt-1 px-3 py-2 border border-[#F1DCC5] bg-white rounded outline-none focus:border-[var(--gl-orange)]"
                >
                  {["paid", "preparing", "shipped", "delivered", "refunded", "cancelled"].map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="text-xs text-[#8A7568]">Tracking number</span>
                <input
                  data-testid="admin-order-tracking-number"
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                  placeholder="e.g. RE123456789PT"
                  className="w-full mt-1 px-3 py-2 border border-[#F1DCC5] bg-white rounded outline-none focus:border-[var(--gl-orange)]"
                />
              </label>
              <label className="block md:col-span-2">
                <span className="text-xs text-[#8A7568]">Tracking URL (optional)</span>
                <input
                  data-testid="admin-order-tracking-url"
                  value={trackingUrl}
                  onChange={(e) => setTrackingUrl(e.target.value)}
                  placeholder="https://tracking.ctt.pt/..."
                  className="w-full mt-1 px-3 py-2 border border-[#F1DCC5] bg-white rounded outline-none focus:border-[var(--gl-orange)]"
                />
              </label>
              <label className="block md:col-span-2">
                <span className="text-xs text-[#8A7568]">Admin notes (internal)</span>
                <textarea
                  data-testid="admin-order-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  className="w-full mt-1 px-3 py-2 border border-[#F1DCC5] bg-white rounded outline-none focus:border-[var(--gl-orange)] resize-none"
                />
              </label>
            </div>
            {NEXT_STATUS[status] && (
              <button
                type="button"
                onClick={() => setStatus(NEXT_STATUS[status])}
                className="mt-3 text-xs underline underline-offset-2 text-[#241C18] hover:text-[var(--gl-orange-hover)]"
              >
                → Advance to {NEXT_STATUS[status]}
              </button>
            )}
          </section>

          {err && <div className="text-sm text-red-600">{err}</div>}
        </div>

        <div className="sticky bottom-0 bg-white border-t border-[#F1DCC5] px-6 py-4 flex flex-wrap items-center justify-between gap-3 rounded-b-2xl">
          {order.status !== "cancelled" && remaining > 0 ? (
            <button
              data-testid="admin-order-refund"
              onClick={openRefund}
              disabled={busy}
              className="text-xs text-red-600 underline underline-offset-2 disabled:opacity-40"
            >
              {alreadyRefunded > 0 ? `Refund more (€${remaining.toFixed(2)} left)` : "Refund via Stripe"}
            </button>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="text-xs underline underline-offset-2 text-[#8A7568]"
            >
              Cancel
            </button>
            <button
              data-testid="admin-order-save"
              onClick={save}
              disabled={busy}
              className="btn-orange disabled:opacity-40"
            >
              {busy ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      </div>

      {/* Partial refund modal — nested inside the order modal */}
      {refundOpen && (
        <div
          className="fixed inset-0 z-[60] bg-[#241C18]/60 flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setRefundOpen(false); }}
          data-testid="admin-refund-modal"
        >
          <div className="bg-white rounded-2xl w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div>
              <div className="font-badge text-[10px] tracking-[0.28em] uppercase text-[#8A7568]">Stripe refund</div>
              <h3 className="font-serif-display text-2xl mt-1 text-[#241C18]">Refund order {order.order_number}</h3>
            </div>

            <div className="rounded-lg bg-[#FFF7E8] border border-[#F1DCC5] p-4 text-sm space-y-1">
              <div className="flex justify-between"><span>Merchandise (subtotal)</span><span>€{subtotal.toFixed(2)}</span></div>
              <div className="flex justify-between"><span>Shipping</span><span>€{shipping.toFixed(2)}</span></div>
              <div className="flex justify-between font-semibold border-t border-[#F1DCC5] pt-1 mt-1"><span>Order total</span><span>€{total.toFixed(2)}</span></div>
              {alreadyRefunded > 0 && (
                <div className="flex justify-between text-[#8B1A1A]"><span>Already refunded</span><span>−€{alreadyRefunded.toFixed(2)}</span></div>
              )}
              <div className="flex justify-between font-semibold"><span>Refundable now</span><span>€{remaining.toFixed(2)}</span></div>
            </div>

            {/* Per-item picker — tick the items the customer sent back.
                Selected items auto-sync the amount input + restock only
                those variants on submit. */}
            {(order.items || []).length > 0 && (
              <div>
                <label className="block text-xs font-badge tracking-[0.24em] uppercase text-[#8A7568] mb-2">
                  Returned items
                </label>
                <div className="max-h-52 overflow-y-auto border border-[#241C18]/15 rounded-md divide-y divide-[#241C18]/10">
                  {(order.items || []).map((it, i) => {
                    const lineTotal = Number(it.unit_price || 0) * Number(it.quantity || 1);
                    const checked = refundSelectedIdx.includes(i);
                    return (
                      <label
                        key={i}
                        data-testid={`admin-refund-item-${i}`}
                        className={`flex items-center gap-3 p-2.5 cursor-pointer text-sm ${checked ? "bg-[#FFF7E8]" : "hover:bg-[#FAF6ED]"}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleItem(i)}
                          className="h-4 w-4"
                          data-testid={`admin-refund-item-checkbox-${i}`}
                        />
                        {it.image ? (
                          <img src={it.image} alt="" className="h-10 w-10 rounded object-cover" />
                        ) : (
                          <div className="h-10 w-10 rounded bg-[#F1DCC5]" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="truncate text-[#241C18]">{it.name}</div>
                          <div className="text-xs text-[#8A7568]">
                            {it.variant_name ? `${it.variant_name} · ` : ""}Qty {it.quantity} · €{Number(it.unit_price || 0).toFixed(2)}
                          </div>
                        </div>
                        <div className="text-sm font-semibold text-[#241C18]">€{lineTotal.toFixed(2)}</div>
                      </label>
                    );
                  })}
                </div>
                {refundSelectedIdx.length > 0 && (
                  <div className="mt-2 text-xs text-[#8A7568]" data-testid="admin-refund-items-summary">
                    {refundSelectedIdx.length} item{refundSelectedIdx.length > 1 ? "s" : ""} selected — restock only these variants and refund the exact merchandise total.
                  </div>
                )}
              </div>
            )}

            <div>
              <label className="block text-xs font-badge tracking-[0.24em] uppercase text-[#8A7568] mb-1">Amount (€)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                max={remaining}
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
                data-testid="admin-refund-amount"
                className="w-full h-11 px-3 border border-[#241C18]/20 rounded-md text-lg font-serif-display"
              />
              <div className="flex flex-wrap gap-2 mt-2">
                <button
                  type="button"
                  data-testid="admin-refund-preset-merch"
                  className="text-[11px] font-badge tracking-[0.14em] uppercase px-3 py-1 border border-[#241C18]/20 rounded-full hover:bg-[#FFF7E8]"
                  onClick={() => { setRefundSelectedIdx([]); setRefundAmount(Math.max(0, Number((subtotal - alreadyRefunded).toFixed(2))).toFixed(2)); setRefundIncludeShipping(false); }}
                >
                  Merchandise only
                </button>
                <button
                  type="button"
                  data-testid="admin-refund-preset-full"
                  className="text-[11px] font-badge tracking-[0.14em] uppercase px-3 py-1 border border-[#241C18]/20 rounded-full hover:bg-[#FFF7E8]"
                  onClick={() => { setRefundSelectedIdx([]); setRefundAmount(remaining.toFixed(2)); setRefundIncludeShipping(true); }}
                >
                  Full (incl. shipping)
                </button>
              </div>
            </div>

            <label className="flex items-start gap-2 text-sm cursor-pointer select-none" data-testid="admin-refund-include-shipping-label">
              <input
                type="checkbox"
                checked={refundIncludeShipping}
                onChange={(e) => setRefundIncludeShipping(e.target.checked)}
                data-testid="admin-refund-include-shipping"
                className="mt-0.5"
              />
              <span>
                <span className="font-medium">Also refund shipping (€{shipping.toFixed(2)})</span>
                <span className="block text-xs text-[#8A7568]">Leave off for customer-initiated returns (legal + our policy).</span>
              </span>
            </label>

            <div>
              <label className="block text-xs font-badge tracking-[0.24em] uppercase text-[#8A7568] mb-1">Reason (optional)</label>
              <input
                type="text"
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
                data-testid="admin-refund-reason"
                placeholder="e.g. kept trousers, returned blouse"
                className="w-full h-10 px-3 border border-[#241C18]/20 rounded-md text-sm"
              />
            </div>

            {err && <div className="text-sm text-red-600" data-testid="admin-refund-error">{err}</div>}

            <div className="flex items-center justify-between pt-2">
              <button
                type="button"
                onClick={() => setRefundOpen(false)}
                className="text-xs underline underline-offset-2 text-[#8A7568]"
                data-testid="admin-refund-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={runRefund}
                disabled={busy}
                data-testid="admin-refund-submit"
                className="px-5 h-10 rounded-full bg-red-600 hover:bg-red-700 text-white font-badge text-[11px] tracking-[0.28em] uppercase disabled:opacity-40"
              >
                {busy ? "Refunding…" : `Refund €${parseFloat(refundAmount || 0).toFixed(2)}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const OrdersDashboard = ({ onLogout }) => {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [dateRange, setDateRange] = useState("30");  // "7" | "30" | "90" | "all"
  const [minAmount, setMinAmount] = useState("");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("newest");       // "newest" | "oldest" | "highest" | "lowest"
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const [ords, s, an] = await Promise.all([
        adminListOrders(statusFilter || undefined),
        adminGetStats(),
        adminGetAnalytics(),
      ]);
      setOrders(ords);
      setStats(s);
      setAnalytics(an);
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        onLogout?.();
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const visibleOrders = useMemo(() => {
    const cutoff = dateRange === "all"
      ? null
      : new Date(Date.now() - Number(dateRange) * 24 * 3600 * 1000);
    const minAmt = Number.parseFloat(minAmount);
    const needle = query.trim().toLowerCase();

    const filtered = orders.filter((o) => {
      if (cutoff && new Date(o.created_at) < cutoff) return false;
      if (!Number.isNaN(minAmt) && Number(o.total) < minAmt) return false;
      if (needle) {
        const hay = `${o.order_number} ${o.customer_email || ""} ${o.customer_name || ""}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });

    const sorted = [...filtered];
    if (sort === "newest") sorted.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    else if (sort === "oldest") sorted.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    else if (sort === "highest") sorted.sort((a, b) => Number(b.total) - Number(a.total));
    else if (sort === "lowest") sorted.sort((a, b) => Number(a.total) - Number(b.total));
    return sorted;
  }, [orders, dateRange, minAmount, query, sort]);

  const exportCsv = () => {
    if (!visibleOrders.length) return;
    const rows = [
      ["order_number", "date", "customer_name", "customer_email", "items", "subtotal", "shipping", "total", "status", "tracking_number", "country", "city"],
      ...visibleOrders.map((o) => [
        o.order_number,
        (o.created_at || "").slice(0, 19),
        o.customer_name || "",
        o.customer_email || "",
        (o.items || []).reduce((s, x) => s + (x.quantity || 0), 0),
        o.subtotal,
        o.shipping_amount,
        o.total,
        o.status,
        o.tracking_number || "",
        o.shipping_address?.country || "",
        o.shipping_address?.city || "",
      ]),
    ];
    const csv = rows.map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const today = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `goya-orders-${today}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 md:p-10">
      {/* KPI Stats */}
      {stats && (() => {
        // Shared subline builder — always includes shipping; also surfaces
        // refunds when there are any, so the "why is the revenue lower than
        // I expect?" question answers itself.
        const sub = (b) => {
          const parts = [`${b.count} order${b.count === 1 ? "" : "s"}`];
          if ((b.shipping || 0) > 0) parts.push(`+${money(b.shipping)} shipping`);
          if ((b.refunded || 0) > 0) parts.push(`−${money(b.refunded)} refunded`);
          return parts.join(" · ");
        };
        return (
          <div data-testid="admin-orders-stats" className="flex flex-wrap gap-3 mb-6">
            <StatCard testid="stat-today"    label="TODAY · MERCHANDISE"    value={money(stats.today.revenue)}    sub={sub(stats.today)} />
            <StatCard testid="stat-week"     label="7 DAYS · MERCHANDISE"   value={money(stats.week.revenue)}     sub={sub(stats.week)} />
            <StatCard testid="stat-month"    label="30 DAYS · MERCHANDISE"  value={money(stats.month.revenue)}    sub={sub(stats.month)} />
            <StatCard testid="stat-alltime"  label="ALL TIME · MERCHANDISE" value={money(stats.all_time.revenue)} sub={sub(stats.all_time)} />
            <StatCard testid="stat-pending"  label="TO SHIP"  value={stats.pending_shipment}         sub="paid + preparing" />
          </div>
        );
      })()}

      {/* Analytics charts */}
      <AnalyticsPanel data={analytics} />

      {/* Rich filters */}
      <div className="mt-8 mb-4 grid grid-cols-2 md:grid-cols-6 gap-2 items-center">
        <input
          data-testid="admin-orders-search"
          placeholder="Search order #, name, email…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="col-span-2 px-3 py-2 border border-[#F1DCC5] bg-white rounded outline-none focus:border-[var(--gl-orange)] text-sm"
        />
        <select
          data-testid="admin-orders-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-[#F1DCC5] bg-white rounded outline-none focus:border-[var(--gl-orange)] text-sm"
        >
          <option value="">All statuses</option>
          {Object.keys(STATUS_STYLES).map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <select
          data-testid="admin-orders-daterange"
          value={dateRange}
          onChange={(e) => setDateRange(e.target.value)}
          className="px-3 py-2 border border-[#F1DCC5] bg-white rounded outline-none focus:border-[var(--gl-orange)] text-sm"
        >
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
          <option value="all">All time</option>
        </select>
        <input
          data-testid="admin-orders-minamount"
          type="number"
          min="0"
          step="10"
          placeholder="Min €"
          value={minAmount}
          onChange={(e) => setMinAmount(e.target.value)}
          className="px-3 py-2 border border-[#F1DCC5] bg-white rounded outline-none focus:border-[var(--gl-orange)] text-sm"
        />
        <select
          data-testid="admin-orders-sort"
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="px-3 py-2 border border-[#F1DCC5] bg-white rounded outline-none focus:border-[var(--gl-orange)] text-sm"
        >
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
          <option value="highest">Highest total</option>
          <option value="lowest">Lowest total</option>
        </select>
      </div>

      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="text-xs text-[#8A7568]">
          <span data-testid="admin-orders-count">{visibleOrders.length}</span> of {orders.length} order{orders.length === 1 ? "" : "s"}
        </div>
        <button
          data-testid="admin-orders-export"
          onClick={exportCsv}
          disabled={!visibleOrders.length}
          className="px-3 py-1.5 rounded-full border border-[#F1DCC5] text-[11px] font-badge tracking-[0.14em] uppercase hover:bg-[#F1DCC5]/50 disabled:opacity-40"
        >
          Export CSV
        </button>
      </div>

      {loading ? (
        <div className="text-[#8A7568]">Loading orders…</div>
      ) : visibleOrders.length === 0 ? (
        <div className="bg-white border border-[#F1DCC5] rounded-2xl p-10 text-center text-[#8A7568]">
          {orders.length === 0
            ? "No orders yet. They\u2019ll appear here as customers check out."
            : "No orders match those filters."}
        </div>
      ) : (
        <div data-testid="admin-orders-table" className="bg-white rounded-2xl border border-[#F1DCC5] overflow-hidden">
          <div className="hidden md:grid grid-cols-[130px_1.5fr_1fr_100px_120px_120px] px-4 py-3 border-b border-[#F1DCC5] font-badge text-[10px] tracking-[0.2em] text-[#8A7568]">
            <div>ORDER</div>
            <div>CUSTOMER</div>
            <div>DATE</div>
            <div>ITEMS</div>
            <div>TOTAL</div>
            <div>STATUS</div>
          </div>
          {visibleOrders.map((o) => (
            <button
              key={o.id}
              type="button"
              data-testid={`admin-order-row-${o.order_number}`}
              onClick={() => setSelected(o)}
              className="w-full text-left grid grid-cols-2 md:grid-cols-[130px_1.5fr_1fr_100px_120px_120px] items-center gap-3 md:gap-2 px-4 py-3 border-b border-[#F1DCC5] last:border-b-0 hover:bg-[#FFE9D6]/40 transition"
            >
              <div className="font-badge text-xs tracking-[0.12em] text-[#241C18]">{o.order_number}</div>
              <div className="min-w-0">
                <div className="font-serif-display text-base text-[#241C18] truncate">{o.customer_name || o.customer_email}</div>
                <div className="text-xs text-[#8A7568] truncate">{o.customer_email}</div>
              </div>
              <div className="text-xs text-[#8A7568]">{new Date(o.created_at).toLocaleDateString()}</div>
              <div className="text-sm text-[#241C18]">{(o.items || []).reduce((s, x) => s + (x.quantity || 0), 0)}</div>
              <div className="text-sm text-[#241C18]">{money(o.total)}</div>
              <div><StatusPill status={o.status} /></div>
            </button>
          ))}
        </div>
      )}

      {selected && (
        <OrderDetail
          order={selected}
          onClose={() => setSelected(null)}
          onSaved={(updated) => {
            setSelected(updated);
            refresh();
          }}
        />
      )}
    </div>
  );
};
